import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { 
  Users, 
  BookOpen, 
  FileText, 
  Bell,
  TrendingUp,
  ArrowRight
} from "lucide-react";

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
        </div>
      </Layout>
    );
  }

  const statCards = [
    { 
      title: 'Total Users', 
      value: stats?.total_users || 0, 
      icon: Users, 
      color: 'bg-violet-100 text-violet-700',
      link: '/admin/users'
    },
    { 
      title: 'Total Courses', 
      value: stats?.total_courses || 0, 
      icon: BookOpen, 
      color: 'bg-emerald-100 text-emerald-600',
      link: '/admin/courses'
    },
    { 
      title: 'Enrollments', 
      value: stats?.total_enrollments || 0, 
      icon: TrendingUp, 
      color: 'bg-orange-100 text-orange-600',
      link: '/admin/courses'
    },
    { 
      title: 'Submissions', 
      value: stats?.total_submissions || 0, 
      icon: FileText, 
      color: 'bg-blue-100 text-blue-600',
      link: '/admin/courses'
    }
  ];

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="admin-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: 'Outfit' }}>
            Admin Dashboard
          </h1>
          <p className="text-stone-600 mt-1">Welcome back, {user?.name}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => (
            <Link key={index} to={stat.link}>
              <Card className="bg-white rounded-xl border border-stone-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer" data-testid={`stat-${stat.title.toLowerCase().replace(' ', '-')}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500 mb-1">{stat.title}</p>
                      <p className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
                        {stat.value}
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color}`}>
                      <stat.icon className="h-6 w-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6">
          <Link to="/admin/courses">
            <Card className="bg-violet-700 text-white rounded-xl hover:bg-violet-800 transition-colors cursor-pointer">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg" style={{ fontFamily: 'Outfit' }}>Manage Courses</h3>
                  <p className="text-violet-200 text-sm">Create and edit courses</p>
                </div>
                <ArrowRight className="h-6 w-6" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/users">
            <Card className="bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors cursor-pointer">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg" style={{ fontFamily: 'Outfit' }}>Manage Users</h3>
                  <p className="text-emerald-200 text-sm">View and manage users</p>
                </div>
                <ArrowRight className="h-6 w-6" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/notifications">
            <Card className="bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors cursor-pointer">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg" style={{ fontFamily: 'Outfit' }}>Send Notifications</h3>
                  <p className="text-orange-200 text-sm">Notify students</p>
                </div>
                <ArrowRight className="h-6 w-6" />
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Recent Activity */}
        <Card className="bg-white rounded-xl border border-stone-100">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit' }}>Recent Enrollments</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_enrollments?.length === 0 ? (
              <p className="text-stone-500 text-center py-8">No recent enrollments</p>
            ) : (
              <div className="space-y-3">
                {stats?.recent_enrollments?.map((enrollment, index) => (
                  <div 
                    key={enrollment.enrollment_id || index}
                    className="flex items-center justify-between p-3 bg-stone-50 rounded-xl"
                  >
                    <div>
                      <p className="font-medium text-stone-900">User: {enrollment.user_id}</p>
                      <p className="text-sm text-stone-500">Course: {enrollment.course_id}</p>
                    </div>
                    <p className="text-xs text-stone-400">
                      {new Date(enrollment.enrolled_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
